import type { AudioBank, SoundAsset } from "./types";
import type { AudioPlayOpts } from "../ports/types";
import type { AudioEngine } from "../core/AudioEngine";
import type { BundleRef } from "./types";

type DevLog = (m: string) => void;

/** How long the server has to start answering a sound request — the boot's budget. */
const FIRST_ANSWER_MS = 10_000;
/** How long a download may go without receiving anything before it counts as frozen. */
const SILENCE_MS = 15_000;

/**
 * Resolves a `bundle_audio` locator to a fetchable URL: `AssetRegistry.resolveAudioUrl`, a local
 * bundle through the asset manifest, a remote one as `<url>/<key>.m4a`. Absent → bundle-located
 * sounds are skipped with a dev log, so audio never blocks boot.
 */
export type AudioBundleUrlResolver = (bundle: BundleRef, key: string) => Promise<string | null>;

export class AudioBankLoader {
  private readonly assets = new Map<string, SoundAsset>();
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly inflight = new Map<string, Promise<void>>();

  constructor(
    private readonly engine: AudioEngine,
    private readonly devLog?: DevLog,
    private readonly resolveBundleAudioUrl?: AudioBundleUrlResolver,
  ) {}

  registerBanks(banks: AudioBank[]): void {
    for (const bank of banks) {
      for (const asset of bank.assets) {
        this.assets.set(asset.id, asset);
      }
    }
  }

  /**
   * Shallow-merges `override` into the registered asset's `defaultOpts`.
   * Returns `false` (and dev-logs) when the id has no registered asset.
   * Used by `createAudioService` to apply `GameAudioOverrides.defaultOptsOverrides`.
   */
  applyDefaultOptsOverride(id: string, override: Partial<AudioPlayOpts>): boolean {
    const asset = this.assets.get(id);
    if (!asset) {
      this.devLog?.(`[audio] defaultOptsOverrides: unknown id="${id}" — override dropped`);
      return false;
    }
    this.assets.set(id, {
      ...asset,
      defaultOpts: { ...(asset.defaultOpts ?? {}), ...override },
    });
    return true;
  }

  getAsset(id: string): SoundAsset | null {
    return this.assets.get(id) ?? null;
  }

  getBuffer(id: string): AudioBuffer | null {
    return this.buffers.get(id) ?? null;
  }

  async preloadOne(id: string): Promise<void> {
    if (this.buffers.has(id)) return;
  
    const existing = this.inflight.get(id);
    if (existing) return existing;
  
    const task = this.preloadOneInternal(id).finally(() => {
      this.inflight.delete(id);
    });
  
    this.inflight.set(id, task);
    return task;
  }
  
  private async preloadOneInternal(id: string): Promise<void> {
    const asset = this.assets.get(id);
    if (!asset) {
      this.devLog?.(`[audio] preloadOne: unknown id=${id}`);
      return;
    }
  
    const locator = asset.locator;
  
    try {
      if (locator.kind === "url") {
        const buf = await this.fetchAndDecode(locator.url);
        this.buffers.set(id, buf);
        return;
      }
  
      if (locator.kind === "bundle_audio") {
        if (!this.resolveBundleAudioUrl) {
          this.devLog?.(`[audio] preloadOne: no bundle resolver — skipped id=${id} key=${locator.key}`);
          return;
        }
        const url = await this.resolveBundleAudioUrl(locator.bundle, locator.key);
        if (!url) {
          throw new Error(`resolveBundleAudioUrl failed (bundle=${JSON.stringify(locator.bundle)}, key=${locator.key})`);
        }

        const buf = await this.fetchAndDecode(url);
        this.buffers.set(id, buf);
        return;
      }
    } catch (e: unknown) {
      const error = e instanceof Error ? e : null;
      this.devLog?.(`[audio] preloadOne FAIL id=${id} err=${error?.message ?? String(e)}`);
      if (error?.stack) this.devLog?.(error.stack);
    }
  }

  // -------------------------
  // Internals
  // -------------------------

  /**
   * A sound file, bounded by silence rather than by its size (user, 2026-09-26): the server has
   * `FIRST_ANSWER_MS` to start answering, then the download is dropped only once nothing has arrived
   * for `SILENCE_MS`. A slow link that keeps delivering is never cut — the 4 MB ambient bed takes
   * about 20 s at 200 KB/s — while a frozen one is let go. Until then nothing bounded it: a frozen
   * download held its sound, the music included, for the whole visit. A dropped file is fetched
   * again the next time it is asked for.
   */
  private async fetchAndDecode(url: string): Promise<AudioBuffer> {
    const controller = new AbortController();
    try {
      const res = await withinMs(fetch(url, { signal: controller.signal }), FIRST_ANSWER_MS, `no answer within ${FIRST_ANSWER_MS} ms url=${url}`);
      if (!res.ok) throw new Error(`fetch failed status=${res.status} url=${url}`);
      if (!res.body) throw new Error(`empty response url=${url}`);
      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let size = 0;
      for (;;) {
        const { done, value } = await withinMs(reader.read(), SILENCE_MS, `nothing received for ${SILENCE_MS} ms url=${url}`);
        if (done) break;
        chunks.push(value);
        size += value.byteLength;
      }
      const data = new Uint8Array(size);
      let offset = 0;
      for (const chunk of chunks) {
        data.set(chunk, offset);
        offset += chunk.byteLength;
      }
      return await this.engine.decodeFromArrayBuffer(data.buffer);
    } catch (error) {
      controller.abort(); // a frozen connection is let go, not left open
      throw error;
    }
  }
}

/** `promise`, or a rejection with `message` once `ms` have passed without it settling. */
function withinMs<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, expiry]).finally(() => clearTimeout(timer));
}
